/**
 * Legacy development-only projection math.
 *
 * These published-average rates are not a clinically reviewed patient model.
 * Keep the calculators available for boundary testing and development review,
 * but do not render their output in production.
 */

export type PotentialLabels = {
  frozen: string;
  thaw: string;
  live: string;
};

export type EmbryoPotentialLabels = PotentialLabels & {
  implant: string;
};

export type EggPotentialLabels = PotentialLabels & {
  fertilize: string;
  blast: string;
  implant: string;
};

export type PotentialStep = {
  label: string;
  count: number;
  color: string;
};

export type CombinedPotentialStep = {
  id: "inventory" | "thaw" | "transfer-ready" | "implant" | "live";
  embryos: number;
  eggs: number;
};

export const CRYO_POTENTIAL_COLORS = {
  embryo: "#38BDF8",
  egg: "#B08030",
  thawEgg: "#E879F9",
  fertilized: "#38BDF8",
  blastocyst: "#818CF8",
  embryoImplant: "#818CF8",
  eggImplant: "#C084FC",
  live: "#F472B6",
} as const;

const RATES = {
  embryo: {
    euploid: {
      thawSurvival: 0.95,
      implantation: 0.63,
      clinicalToLiveBirth: 0.85,
    },
    untested: {
      thawSurvival: 0.95,
      implantation: 0.42,
      clinicalToLiveBirth: 0.8,
    },
  },
  egg: {
    thawSurvival: 0.87,
    fertilization: 0.75,
    blastocyst: 0.45,
    implantation: 0.45,
    clinicalToLiveBirth: 0.8,
  },
} as const;

function normalizeCount(value: number) {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

function roundWithin(value: number, upperBound: number) {
  return Math.min(upperBound, normalizeCount(value));
}

export function computeEmbryoPotential(
  euploidCount: number,
  untestedCount: number,
  labels: EmbryoPotentialLabels,
): PotentialStep[] {
  const euploid = normalizeCount(euploidCount);
  const untested = normalizeCount(untestedCount);
  const totalEmbryos = euploid + untested;
  if (totalEmbryos === 0) return [];

  const euploidThaw = euploid * RATES.embryo.euploid.thawSurvival;
  const untestedThaw = untested * RATES.embryo.untested.thawSurvival;
  const thawTotal = roundWithin(euploidThaw + untestedThaw, totalEmbryos);

  const euploidImplant = euploidThaw * RATES.embryo.euploid.implantation;
  const untestedImplant = untestedThaw * RATES.embryo.untested.implantation;
  const implantTotal = roundWithin(euploidImplant + untestedImplant, thawTotal);

  const euploidLive = euploidImplant * RATES.embryo.euploid.clinicalToLiveBirth;
  const untestedLive =
    untestedImplant * RATES.embryo.untested.clinicalToLiveBirth;
  const liveTotal = roundWithin(euploidLive + untestedLive, implantTotal);

  return [
    {
      label: labels.frozen,
      count: totalEmbryos,
      color: CRYO_POTENTIAL_COLORS.embryo,
    },
    { label: labels.thaw, count: thawTotal, color: "#6EE7B7" },
    {
      label: labels.implant,
      count: implantTotal,
      color: CRYO_POTENTIAL_COLORS.embryoImplant,
    },
    { label: labels.live, count: liveTotal, color: CRYO_POTENTIAL_COLORS.live },
  ];
}

export function computeEggPotential(
  eggCount: number,
  labels: EggPotentialLabels,
): PotentialStep[] {
  const eggs = normalizeCount(eggCount);
  if (eggs === 0) return [];

  const thaw = eggs * RATES.egg.thawSurvival;
  const thawCount = roundWithin(thaw, eggs);
  const fertilized = thaw * RATES.egg.fertilization;
  const fertilizedCount = roundWithin(fertilized, thawCount);
  const blasts = fertilized * RATES.egg.blastocyst;
  const blastCount = roundWithin(blasts, fertilizedCount);
  const implanted = blasts * RATES.egg.implantation;
  const implantCount = roundWithin(implanted, blastCount);
  const live = implanted * RATES.egg.clinicalToLiveBirth;
  const liveCount = roundWithin(live, implantCount);

  return [
    {
      label: labels.frozen,
      count: eggs,
      color: CRYO_POTENTIAL_COLORS.egg,
    },
    {
      label: labels.thaw,
      count: thawCount,
      color: CRYO_POTENTIAL_COLORS.thawEgg,
    },
    {
      label: labels.fertilize,
      count: fertilizedCount,
      color: CRYO_POTENTIAL_COLORS.fertilized,
    },
    {
      label: labels.blast,
      count: blastCount,
      color: CRYO_POTENTIAL_COLORS.blastocyst,
    },
    {
      label: labels.implant,
      count: implantCount,
      color: CRYO_POTENTIAL_COLORS.eggImplant,
    },
    { label: labels.live, count: liveCount, color: CRYO_POTENTIAL_COLORS.live },
  ];
}

/**
 * Normalize the two source-specific paths into shared clinical stages.
 * Eggs only become comparable to frozen embryos once they reach blastocyst
 * stage; raw egg and embryo counts are therefore never combined as outcomes.
 */
export function computeCombinedPotential(
  embryoSteps: PotentialStep[],
  eggSteps: PotentialStep[],
): CombinedPotentialStep[] {
  const embryoAt = (index: number) => embryoSteps[index]?.count ?? 0;
  const eggAt = (index: number) => eggSteps[index]?.count ?? 0;

  if (embryoSteps.length === 0 && eggSteps.length === 0) return [];

  return [
    {
      id: "inventory",
      embryos: embryoAt(0),
      eggs: eggAt(0),
    },
    {
      id: "thaw",
      embryos: embryoAt(1),
      eggs: eggAt(1),
    },
    {
      id: "transfer-ready",
      embryos: embryoAt(1),
      eggs: eggAt(3),
    },
    {
      id: "implant",
      embryos: embryoAt(2),
      eggs: eggAt(4),
    },
    {
      id: "live",
      embryos: embryoAt(3),
      eggs: eggAt(5),
    },
  ];
}
